import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  SERVER_TRAINING_CHECKLIST_PATH,
  SERVER_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${SERVER_TRAINING_CHECKLIST_PATH}`;
const pillarUrl = `https://speakyourmenu.com${SERVER_TRAINING_PILLAR_PATH}`;
const pageTitle = "Server Onboarding Checklist for Restaurants | Speak Your Menu";
const pageDescription = "A practical server onboarding checklist for restaurants that need clearer first-week expectations, faster confidence, and stronger manager follow-through.";

const checklistSections = [
  {
    title: "Define day-one expectations",
    items: [
      "Clarify what every new server must know before their first busy shift.",
      "Set the baseline for menu descriptions, allergens, pacing, and escalation when they do not know an answer.",
      "Decide which guest-facing situations new hires should be ready to handle first.",
    ],
  },
  {
    title: "Build the first-week practice loop",
    items: [
      "Use short practice blocks outside live service instead of relying on shadow shifts alone.",
      "Repeat the same high-risk concepts across multiple sessions so recall improves before pressure spikes.",
      "Make sure new hires practice how to talk about the menu, not just how to memorize it.",
    ],
  },
  {
    title: "Coach the weak spots early",
    items: [
      "Track where hesitation appears in the first few shifts.",
      "Use pre-shift reinforcement to revisit the exact questions or dishes that keep surfacing.",
      "Do not wait for a guest complaint to reveal the knowledge gap.",
    ],
  },
  {
    title: "Protect consistency after week one",
    items: [
      "Check whether the new server still relies too heavily on veterans for basic answers.",
      "Reinforce guest-facing language, service flow, and common modifier questions in week two.",
      "Convert the strongest first-week steps into a repeatable onboarding standard for the next hire.",
    ],
  },
];

export default function ServerTrainingChecklistPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Server Training Program", item: pillarUrl },
      { "@type": "ListItem", position: 3, name: "Server Onboarding Checklist", item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Checklist</span>
              <h1 className="homepage-hero-title resource-hero-title">A server onboarding checklist your managers can use from day one.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                Use this checklist to improve first-week expectations, reinforce new-server confidence, and reduce the guesswork that makes onboarding inconsistent.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={SERVER_TRAINING_PILLAR_PATH} className="resource-secondary-link">Read the full guide</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Use it weekly</span>
            <h2 className="homepage-section-title resource-section-title">Server onboarding checklist</h2>
            <div className="resource-checklist-stack">
              {checklistSections.map((section) => (
                <article key={section.title} className="homepage-surface-card resource-card">
                  <h3 className="homepage-card-title">{section.title}</h3>
                  <ul className="resource-bullet-list">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Why it matters</span>
              <h2 className="homepage-section-title resource-section-title">Early consistency is what keeps new-server confidence from collapsing.</h2>
              <p className="homepage-section-body resource-section-body">
                Most onboarding feels fine until the first busy shifts expose what did not stick. A checklist gives managers a repeatable structure for what to reinforce before those gaps hit the floor.
              </p>
            </article>
            <article className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Go deeper</span>
              <h2 className="homepage-card-title resource-aside-title">Need the full system behind the checklist?</h2>
              <p className="homepage-card-copy">The full guide explains how to structure server training beyond shadowing and build a stronger first-week operating rhythm.</p>
              <Link to={SERVER_TRAINING_PILLAR_PATH} className="homepage-primary-cta resource-inline-cta">Read the guide</Link>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}